import React, { useCallback, useEffect, useRef, useState } from 'react';

import { postSlackMessage, sendAction } from '../../utils/api';

import './Feedback.css';

function Feedback({ handleError, handleFeedback }) {
  const QUESTIONS = [
    {
      param: 'chat',
      type: 'stars',
      label: 'How would you rate the answers given in the <strong>Chat</strong>?',
      values: [1, 2, 3, 4, 5],
      divider: true
    },
    {
      param: 'path',
      type: 'stars',
      label: 'How would you rate the suggested questions in the <strong>Path</strong> section?',
      values: [1, 2, 3, 4, 5]
    },
    {
      param: 'profile',
      type: 'stars',
      label: 'How would you rate the <strong>Profile</strong> section?',
      values: [1, 2, 3, 4, 5]
    },
    {
      param: 'overall',
      type: 'stars',
      label: 'How would you rate your <strong>overall</strong> experience?',
      values: [1, 2, 3, 4, 5]
    },
    {
      param: 'enjoyed',
      type: 'textarea',
      label: 'What are we getting right?',
      sublabel: 'Tell us what you enjoyed most',
      placeholder: 'Type your message...',
      divider: true
    },
    {
      param: 'disliked',
      type: 'textarea',
      label: 'What was not good?',
      sublabel: 'Tell us what you disliked most',
      placeholder: 'Type your message...'
    },
    {
      param: 'problems',
      type: 'textarea',
      label: 'Have you had any problems while using the app?',
      sublabel: 'Tell us what went wrong',
      placeholder: 'Type your message...'
    },
    {
      param: 'description',
      type: 'radio',
      label: 'Which of these best describes you?',
      sublabel: 'Choose one',
      values: ['High school student (18+)', 'College student', 'CSF advisor or coach', 'In a job training program', 'Other']
    }
  ];

  const formRef = useRef(null);
  const [hasHover, setHasHover] = useState(null);
  const [stars, setStars] = useState({});
  const [starsHover, setStarsHover] = useState({});

  const handleHover = useCallback((param) => {
    setHasHover(param);
  }, []);

  const handleStarClick = useCallback((param, index) => {
    setStars(prevStars => {
      prevStars[param] = index;

      return {...prevStars};
    });
  }, []);

  const handleStarHover = useCallback((param, index) => {
    setStarsHover(prevStars => {
      prevStars[param] = index;

      return {...prevStars};
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formRef.current) {
      const formData = new FormData(formRef.current);
      let data = {};
      let message = '';

      QUESTIONS.forEach(question => {
        if (question.type === 'stars') {
          if (stars[question.param] !== undefined) {
            const value = question.values[stars[question.param]];

            data[question.param] = value;
            message += '*' + question.label + '*\n' + value + '\n';
          }
        } else {
          const value = formData.get(question.param);

          if (value) {
            data[question.param] = value;
            message += '*' + question.label + '*\n' + value + '\n';
          }
        }
      });
      
      if (Object.values(data).length) {
        try {
          if (message) {
            await postSlackMessage('*New Feedback Submission*\n\n' + message.replace(/(<([^>]+)>)/gi, ''));
          }
        } catch (e) {
          handleError(e);
        }

        try {
          const response = await sendAction('feedback', null, data);

          if (response !== null) {
            handleFeedback(false);
          }
        } catch (e) {
          handleError(e);
        }
      } else {
        handleFeedback(false);
      }
    }
  };

  const triggerSubmit = useCallback(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleFeedback(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleFeedback]);

  return (
    <div id="feedback-container" className="popup-overlay">
      <div className="popup">
        <div className="popup-title">We need your feedback to get better.</div>
        <div className="popup-description">
          <p>Please tell us what you think. For assistance please email <a href="mailto:support@campusevolve.ai">support@campusevolve.ai</a>.</p>
        </div>
        <form ref={formRef} onSubmit={handleSubmit}>
          {QUESTIONS.map((question, qi) =>
            <React.Fragment key={qi}>
              {question.divider ? (
                <hr />
              ) : null}
              <div key={question.param} className="question">
                <label dangerouslySetInnerHTML={{__html: question.label}} />
                {question.sublabel ? (
                  <div className="sublabel">{question.sublabel}</div>
                ) : null}
                {question.type === 'radio' ? (
                  <div className="radios">
                    {question.values.map((value, index) =>
                      <div key={index} className="radio">
                        <input type="radio" name={question.param} value={value} />
                        <span>{value}</span>
                      </div>
                    )}
                  </div>
                ) : null}
                {question.type === 'stars' ? (
                  <div className="stars" onMouseOut={() => handleHover(null)} onMouseOver={() => handleHover(question.param)}>
                    {question.values.map((value, index) =>
                      <img key={index} src={process.env.PUBLIC_URL + '/assets/icons/feedback-star-' + ((hasHover === question.param && starsHover[question.param] !== undefined && starsHover[question.param] >= index) || (hasHover !== question.param && stars[question.param] !== undefined && stars[question.param] >= index) ? 'full' : 'empty') + '.svg'} alt="Star" onClick={() => handleStarClick(question.param, index)} onMouseOver={() => handleStarHover(question.param, index)} />
                    )}
                  </div>
                ) : null}
                {question.type === 'text' ? (
                  <input type="text" name={question.param} />
                ) : null}
                {question.type === 'textarea' ? (
                  <textarea name={question.param} placeholder="Type your message..."></textarea>
                ) : null}
              </div>
            </React.Fragment>
          )}
        </form>
        <hr />
        <div className="popup-callout">Thanks again for your help!</div>
        <div className="popup-actions">
          <div className="popup-action popup-action-secondary" role="button" tabIndex="0" onClick={() => handleFeedback(false)}>Cancel</div>
          <div className="popup-action popup-action-primary" role="button" tabIndex="0" onClick={triggerSubmit}>Send Feedback</div>
        </div>
      </div>
    </div>
  );
}

export default Feedback;
